import React, { ChangeEvent, useState, useEffect } from 'react';

import {
  Flex,
  Button,
  Input,
  FormLabel,
  FormControl,
  FormErrorMessage,
  AttentionMessage,
  VStack,
} from '@tq/ui-components';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';

import { isEmailValid } from 'tools';
import { useUpsertParentOrganisation } from 'api';
import { organisationRequestState, organisationIdState } from 'store';
import { useMessageProps } from '../hooks';
import { Organisation } from 'types';

export type OrganisationDetailsPanelProps = {
  organisation?: Organisation;
};

export const OrganisationDetailsPanel: React.FC<OrganisationDetailsPanelProps> = ({
  organisation,
}) => {
  const { t } = useTranslation('manage_organisation');
  const latestSubmit = useRecoilValue(organisationRequestState);
  const organisationId = useRecoilValue(organisationIdState);

  const [name, setName] = useState<string>(organisation?.organisationName ?? '');
  const [nameIsInvalid, setNameIsInvalid] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(organisation?.email ?? '');
  const [emailIsInvalid, setEmailIsInvalid] = useState<boolean>(false);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);

  const { mutate: upsertResult, status, error, isLoading } = useUpsertParentOrganisation();
  const messageProps = useMessageProps(status, error, organisationId, latestSubmit);

  const isNameValid = (value: string) => value.length > 0;

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.currentTarget.value);
  };

  const handleNameBlur = (e: ChangeEvent<HTMLInputElement>) => {
    setNameIsInvalid(!isNameValid(e.currentTarget.value));
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setEmail(value);
    if (value === '') setEmailIsInvalid(false);
  };

  const handleEmailBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    if (value === '') setEmailIsInvalid(false);
    else setEmailIsInvalid(!isEmailValid(value));
  };

  useEffect(() => {
    if (name === '') setSubmitDisabled(true);
    else setSubmitDisabled(nameIsInvalid || emailIsInvalid);
  }, [nameIsInvalid, emailIsInvalid, name, email]);

  useEffect(() => {
    if (latestSubmit && name === latestSubmit.organisationName && email === latestSubmit.email)
      setSubmitDisabled(true);
  }, [latestSubmit, setSubmitDisabled, name, email]);

  const handleSubmit = () => {
    const payload = {
      organisationName: name,
      email: email,
      parentOrganisationId: organisationId,
    };
    upsertResult(payload);
  };

  return (
    <VStack gap={5} pb={4}>
      <Flex width='100%' gap={8}>
        <FormControl isInvalid={nameIsInvalid} flexGrow={1}>
          <FormLabel>{`${t('name_input_label', 'Organisation name')}:`}</FormLabel>
          <Input
            value={name}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            placeholder={t('name_input_placeholder', 'Name')}
            data-testid='organisation-name-input'
          />
          <FormErrorMessage>
            {t('name_input_error', 'Please add an organisation name to continue')}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={emailIsInvalid} flexGrow={1}>
          <FormLabel>{`${t('email_input_label', 'Organisation email')}:`}</FormLabel>
          <Input
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            placeholder={t('email_input_placeholder', 'Email')}
            data-testid='organisation-email-input'
          />
          <FormErrorMessage>
            {t('email_input_error', 'Please enter a valid email address')}
          </FormErrorMessage>
        </FormControl>
      </Flex>
      <Flex
        width='100%'
        justifyContent={messageProps?.messageTitle ? 'space-between' : 'flex-end'}
      >
        {messageProps?.messageTitle ? (
          <AttentionMessage
            width='max-content'
            status={messageProps.messageStatus}
            title={messageProps.messageTitle}
            variant='inline'
          />
        ) : null}
        <Button isLoading={isLoading} disabled={submitDisabled} onClick={handleSubmit}>
          {organisationId
            ? t('update_button_text', 'Update organisation')
            : t('create_button_text', 'Create organisation')}
        </Button>
      </Flex>
    </VStack>
  );
};
